import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Engineer, fetchEngineer } from '../models/engineer';
import { Maybe } from '../types';
import { useAuth } from './Authentication';
import { ChildrenType } from './types';

type EngineerContextValue = {
  engineer?: Engineer | null;
  fetching: Boolean;
  reload: (options?: { noLoading?: boolean }) => Promise<Maybe<Engineer>>;
};

const INITIAL_CONTENT_VALUE: EngineerContextValue = {
  fetching: true,
  reload: () => Promise.resolve(undefined),
};

const EngineerContext = React.createContext<EngineerContextValue>(
  INITIAL_CONTENT_VALUE,
);

export function useEngineer() {
  return useContext(EngineerContext);
}

export default function EngineerProvider({ children }: { children: ChildrenType }) {
  const [value, setValue] = useState<EngineerContextValue>(INITIAL_CONTENT_VALUE);
  const { user } = useAuth();
  const username = user?.username;
  const reload = useCallback<EngineerContextValue['reload']>(
    async ({ noLoading } = {}): Promise<Maybe<Engineer>> => {
      if (!username) {
        setValue({ ...INITIAL_CONTENT_VALUE, fetching: false });
        return undefined;
      }
      setValue((prev) => ({
        ...INITIAL_CONTENT_VALUE,
        ...prev,
        ...(noLoading && { fetching: false }),
      }));
      try {
        const engineer = await fetchEngineer(username);
        setValue({ engineer, fetching: false, reload });
        return engineer;
      } catch (err) {
        setValue({ ...INITIAL_CONTENT_VALUE, fetching: false });
        return undefined;
      }
    },
    [username],
  );
  useEffect(
    () => {
      reload();
    },
    [reload],
  );
  return (
    <EngineerContext.Provider value={value}>
      {children}
    </EngineerContext.Provider>
  );
}
